import type { ShelfId } from "@hybrid-iptv/tmdb-client";

/**
 * Home's genre rows (Family, Drama, Kids, Action & Adventure, Comedy, Sci-Fi,
 * Romance): the catalog side.
 *
 * Each row is first filled from TMDB's US titles for that genre that the
 * provider has. When too few of those are in the catalog, the row is topped up
 * from the catalog's own genre categories, which panels name by genre ("drama",
 * "EN | Action", "sci-fi-fantasy"). Foreign-language categories sit apart on
 * the panels seen so far ("fr-french-movies", "kr-korean-series"), so skipping
 * them, and any title not in Latin script, is how the top-up stays US content.
 *
 * Rows rotate once a day: the same day always gives the same rows, so a
 * reload does not reshuffle what someone was about to pick.
 */

export interface ShelfCatalogItem {
  id: string;
  rawTitle: string;
  categoryId: string;
  tmdbId?: number;
  posterUrl?: string;
  rating?: number;
}

const CATEGORY_PATTERNS: Record<"movie" | "tv", Record<ShelfId, RegExp>> = {
  movie: {
    family: /\bfamily\b/,
    drama: /\bdramas?\b/,
    kids: /\b(kids?|children|childrens|animation|animated|cartoons?)\b/,
    action: /\b(action|adventure)\b/,
    comedy: /\b(comedy|comedies)\b/,
    scifi: /\b(sci-?fi|science-?fiction)\b/,
    romance: /\b(romance|romantic)\b/,
  },
  tv: {
    family: /\bfamily\b/,
    drama: /\bdramas?\b/,
    // Not "animation": for series that is Family Guy and Rick and Morty.
    kids: /\b(kids?|children|childrens|cartoons?)\b/,
    action: /\b(action|adventure)\b/,
    comedy: /\b(comedy|comedies|sitcoms?)\b/,
    scifi: /\b(sci-?fi|science-?fiction|fantasy)\b/,
    romance: /\b(romance|romantic)\b/,
  },
};

const FOREIGN_WORDS =
  /\b(french|german|spanish|italian|korean|k-?drama|arabic|portuguese|brazil\w*|finland|finnish|india|indian|bollywood|hindi|tamil|telugu|punjabi|urdu|latin|latino|netherlands?|dutch|turkish|polish|russian|chinese|japanese|anime|filipino|swedish|danish|norwegian|greek|persian|thai|vietnamese|african|nollywood)\b/;
/** A leading two-letter country or language code other than the US / UK / English ones: "fr-", "kr-", "de-".
 * Two letters only: three-letter starts are words as often as codes ("war-politics", "sci-fi"). */
const FOREIGN_PREFIX = /^(?!(?:en|us|uk)-)[a-z]{2}-/;
const ALLOWED_PREFIX_WORDS = /^(tv|hd|my|ny|la|dc|tn|tx)-/;
const NON_LATIN = /[^\p{Script=Latin}\p{N}\p{P}\p{S}\s]/u;

export function isForeignCategory(categoryId: string): boolean {
  const slug = categoryId.toLowerCase();
  if (FOREIGN_WORDS.test(slug)) return true;
  return FOREIGN_PREFIX.test(slug) && !ALLOWED_PREFIX_WORDS.test(slug);
}

/** The rows a category's titles can top up, from its name. */
export function shelvesForCategory(categoryId: string, media: "movie" | "tv", shelves: readonly ShelfId[]): ShelfId[] {
  const slug = categoryId.toLowerCase();
  return isForeignCategory(slug) ? [] : shelves.filter((shelf) => CATEGORY_PATTERNS[media][shelf].test(slug));
}

/** Whether a title may top up a row at all (its category must fit too). */
export function isShelfCandidate(item: { rawTitle: string; tmdbId?: number; posterUrl?: string }): boolean {
  return Boolean(item.tmdbId && item.tmdbId > 0 && item.posterUrl) && !NON_LATIN.test(item.rawTitle);
}

/**
 * The catalog's candidates for each row, built once per catalog. Only titles
 * with a TMDB id and a poster: the row opens the same detail view as the TMDB
 * titles, and a tile without art is not worth featuring.
 */
export function buildShelfCandidates<T extends ShelfCatalogItem>(
  catalog: readonly T[],
  media: "movie" | "tv",
  shelves: readonly ShelfId[],
): Map<ShelfId, T[]> {
  const byShelf = new Map<ShelfId, T[]>(shelves.map((shelf) => [shelf, []]));
  const categoryShelves = new Map<string, ShelfId[]>();
  for (const item of catalog) {
    if (!(item.tmdbId && item.tmdbId > 0) || !item.posterUrl) continue;
    let fits = categoryShelves.get(item.categoryId);
    if (!fits) {
      fits = shelvesForCategory(item.categoryId, media, shelves);
      categoryShelves.set(item.categoryId, fits);
    }
    if (fits.length === 0 || !isShelfCandidate(item)) continue;
    for (const shelf of fits) byShelf.get(shelf)!.push(item);
  }
  return byShelf;
}

/** Days since the epoch in the viewer's own time zone, so rows turn over at local midnight. */
export function localDayIndex(nowMs: number): number {
  return Math.floor((nowMs - new Date(nowMs).getTimezoneOffset() * 60_000) / 86_400_000);
}

/** Mirrored natively (CatalogStore.shelfHash) for the APK's genre-row top-up. */
export function hash(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** A shuffle that is the same for the same seed: a day's rows stay put. */
export function seededShuffle<T>(items: readonly T[], seed: string, keyOf: (item: T) => string | number): T[] {
  return items
    .map((item) => ({ item, order: hash(`${seed}:${keyOf(item)}`) }))
    .sort((a, b) => a.order - b.order)
    .map((entry) => entry.item);
}

/**
 * Up to `count` catalog titles for a row, skipping TMDB ids already on Home.
 * Well-rated titles (6.5+) come first, shuffled for the day, then the rest.
 */
export function pickShelfFill<T extends ShelfCatalogItem>(
  candidates: readonly T[],
  usedTmdbIds: ReadonlySet<number>,
  count: number,
  seed: string,
): T[] {
  if (count <= 0) return [];
  const fresh = candidates.filter((item) => !usedTmdbIds.has(item.tmdbId!));
  const good = seededShuffle(fresh.filter((item) => (item.rating ?? 0) >= 6.5), seed, (item) => item.id);
  const rest = good.length >= count ? [] : seededShuffle(fresh.filter((item) => (item.rating ?? 0) < 6.5), seed, (item) => item.id);
  const picked: T[] = [];
  const seen = new Set<number>();
  for (const item of [...good, ...rest]) {
    if (seen.has(item.tmdbId!)) continue;
    seen.add(item.tmdbId!);
    picked.push(item);
    if (picked.length === count) break;
  }
  return picked;
}

/** "Toy Story 5 - 2026" → "Toy Story 5", for a tile label. */
export function displayTitle(rawTitle: string): string {
  return rawTitle.replace(/\s*[-–(]\s*(?:19|20)\d{2}\)?\s*.*$/, "").trim() || rawTitle;
}
